import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, m } from 'framer-motion';
import { CircleAlert, CircleCheck, Info, ServerOff, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useUiStore } from '@/store/uiStore';
import { MiniIcon } from './Surface';
import { Button } from './Button';

export function Spinner({ size = 46, className }: { size?: number; className?: string }) {
  return (
    <span
      className={cn(
        'inline-block animate-bd-spin rounded-full border-[3px] border-brand/25 border-t-brand-deep',
        className,
      )}
      style={{ width: size, height: size }}
      role="status"
      aria-label="Загрузка"
    />
  );
}

/** Скелетон списка на время первой загрузки экрана. */
export function Skeleton({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('grid gap-2', className)} aria-hidden>
      {Array.from({ length: lines }, (_, index) => (
        <span
          key={index}
          className="relative h-2 overflow-hidden rounded-full bg-muted/15"
          style={{ width: `${100 - index * 18}%` }}
        >
          <span className="absolute inset-0 -translate-x-full animate-bd-shimmer bg-[linear-gradient(90deg,transparent,rgba(255,255,255,.35),transparent)]" />
        </span>
      ))}
    </div>
  );
}

export function PanelSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="grid gap-2.5">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="rounded-panel border border-line/70 bg-surface p-3.5 shadow-panel">
          <Skeleton lines={2} />
        </div>
      ))}
    </div>
  );
}

export interface StateViewProps {
  icon: ReactNode;
  title: string;
  description: ReactNode;
  tone?: 'brand' | 'lime' | 'soft' | 'muted';
  primary?: ReactNode;
  secondary?: ReactNode;
  children?: ReactNode;
}

/** Единый шаблон пустых состояний, ошибок и финальных экранов. */
export function StateView({
  icon,
  title,
  description,
  tone = 'soft',
  primary,
  secondary,
  children,
}: StateViewProps) {
  return (
    <m.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex min-h-[60vh] flex-col items-center justify-center px-2 text-center"
    >
      <MiniIcon tone={tone} size={60} className="rounded-2xl">
        {icon}
      </MiniIcon>
      <h1 className="mt-4 text-[22px] font-medium tracking-[-0.03em]">{title}</h1>
      <p className="mt-2 max-w-[300px] text-[13px] leading-[1.45] text-muted">{description}</p>
      {children ? <div className="mt-4 w-full">{children}</div> : null}
      {primary || secondary ? (
        <div className="mt-5 grid w-full gap-2">
          {primary}
          {secondary}
        </div>
      ) : null}
    </m.div>
  );
}

/**
 * Экран «данные не загрузились».
 *
 * Отдельный компонент, потому что таких мест девять, и раньше каждое решало
 * само: где-то был выход в поддержку, где-то только повтор — человек в тупике
 * на одном экране получал помощь, а на другом нет.
 */
export function LoadFailure({
  title,
  description,
  onRetry,
  retryLabel = 'Попробовать снова',
  secondary,
}: {
  title: string;
  /** Текст ошибки от сервера: он точнее общей формулировки. */
  description?: string | null;
  onRetry(): void;
  retryLabel?: string;
  /** Чем заменить выход в поддержку, если рядом есть более полезный путь. */
  secondary?: ReactNode;
}) {
  const navigate = useNavigate();

  return (
    <StateView
      icon={<ServerOff className="h-6 w-6" aria-hidden />}
      title={title}
      description={description || 'Проверьте связь и попробуйте ещё раз.'}
      primary={
        <Button variant="primary" block onClick={onRetry}>
          {retryLabel}
        </Button>
      }
      secondary={
        secondary ?? (
          <Button variant="ghost" block onClick={() => navigate('/help')}>
            Написать в поддержку
          </Button>
        )
      }
    />
  );
}

const toastIcons = {
  info: Info,
  success: CircleCheck,
  error: CircleAlert,
};

export function Toaster() {
  const toasts = useUiStore((state) => state.toasts);
  const dismiss = useUiStore((state) => state.dismissToast);

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--bd-tabbar-height)+var(--bd-safe-bottom)+12px)] z-50 flex flex-col items-center gap-2 px-4">
      <AnimatePresence initial={false}>
        {toasts.map((toast) => {
          const Icon = toastIcons[toast.tone];
          return (
            <m.div
              key={toast.id}
              layout
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              className={cn(
                'pointer-events-auto flex w-full max-w-[360px] items-center gap-2.5 rounded-control px-3.5 py-3 text-[13px] shadow-panel',
                toast.tone === 'error' ? 'bg-brand-deep text-white' : 'bg-ink text-canvas',
              )}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden />
              <span className="flex-1 leading-snug">{toast.text}</span>
              <button
                type="button"
                aria-label="Закрыть"
                onClick={() => dismiss(toast.id)}
                className="shrink-0 opacity-70"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </m.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
